import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useAxios from '../axios/useAxios';
import { auth } from '../firebase/firebaseConfig';
import { mergeAccounts, saveUser } from '../actions/userActions';
import { authenticate } from '../actions/authenticateActions';
import { notifyWarning } from '../utils/Notify';
import HandleGoogle from '../pages/auth/login/HandleGoogle';
import GetUserInfo from '../pages/auth/GetUserInfo';
import { ACCOUNT } from '../constants/routes';
import AnimatedLoader from '../components/AnimatedLoader/AnimatedLoader';

const GoogleAuthContext = createContext(null);

export const GoogleAuthProvider = ({ children }) => {
  const [isLoadingGoogleAuth, setIsLoadingGoogleAuth] = useState(false);
  const axiosInstance = useAxios();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const { cartItems } = useSelector((state) => state.cart);
  const userRef = useRef(user);
  const cartItemsRef = useRef(cartItems);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    userRef.current = user;
    cartItemsRef.current = cartItems;
  }, [user, cartItems]);

  const handleRedirectInfoAuthentication = useCallback(async () => {
    if (isProcessingRef.current) {
      console.info('Already processing authentication, skipping...');
      return;
    }

    isProcessingRef.current = true;

    try {
      console.info('Starting Google redirect authentication...');
      const data = await GetUserInfo(auth, userRef.current);

      if (!data) {
        console.info('No redirect data found');
        setIsLoadingGoogleAuth(false);
        isProcessingRef.current = false;
        return;
      }

      console.info('Authenticating with Google data:', data.newResult.profile.email);
      await authenticate(
        axiosInstance,
        data.newResult.profile.email,
        data.result.user.accessToken,
      );

      console.info('Saving user to Redux store...');
      await dispatch(
        saveUser(
          data.newResult.profile,
          axiosInstance,
          cartItemsRef.current,
          navigate,
        ),
      );

      console.info('Google authentication successful, navigating to ACCOUNT...');

      setIsLoadingGoogleAuth(false);
      isProcessingRef.current = false;

      navigate(ACCOUNT);
    } catch (error) {
      console.error('Google authentication error:', error);
      setIsLoadingGoogleAuth(false);
      isProcessingRef.current = false;

      if (error.response?.data?.errorCode === 'USER_FID_CARD_ALREADY_USED') {
        notifyWarning(
          'Oups',
          'L\'email ne correspond à la carte de fidélité',
          'Ok',
        );
      } else {
        notifyWarning(
          'Oups',
          'Une erreur est survenue lors de ta connexion.',
          'Ok',
        );
      }
    }
  }, [axiosInstance, dispatch, navigate]);

  const handleAccountMerge = useCallback(async (firebaseUser) => {
    if (isProcessingRef.current) {
      console.info('Already processing merge, skipping...');
      return;
    }

    isProcessingRef.current = true;

    try {
      let authType = '';
      localStorage.removeItem('fusionFirstAccount');

      if (firebaseUser.displayName) {
        authType = 'google';
      } else {
        authType = 'email';
      }

      console.info('Merging accounts with type:', authType);
      const result = await mergeAccounts(
        axiosInstance,
        userRef.current._id,
        firebaseUser,
        authType,
        dispatch,
        userRef.current,
        navigate,
      );

      setIsLoadingGoogleAuth(false);
      isProcessingRef.current = false;

      if (result) {
        navigate(ACCOUNT);
      }
    } catch (error) {
      console.error('Account merge error:', error);
      setIsLoadingGoogleAuth(false);
      isProcessingRef.current = false;
    }
  }, [axiosInstance, dispatch, navigate]);

  useEffect(() => {
    console.info('Setting up GLOBAL onAuthStateChanged listener...');

    const unsubscribeAuthStateChanged = auth.onAuthStateChanged(
      async (firebaseUser) => {
        console.info('Auth state changed:', {
          hasFirebaseUser: !!firebaseUser,
          firebaseEmail: firebaseUser?.email,
          hasUserId: !!userRef.current?._id,
          hasFusionFlag: !!localStorage.getItem('fusionFirstAccount'),
          isProcessing: isProcessingRef.current,
        });

        if (!firebaseUser) {
          console.info('No Firebase user, skipping authentication');
          setIsLoadingGoogleAuth(false);
          return;
        }

        if (isProcessingRef.current) {
          console.info('Already processing, ignoring duplicate event');
          return;
        }

        const needsProcessing =
          (localStorage.getItem('fusionFirstAccount') && firebaseUser?.email && firebaseUser?.displayName) ||
          (!userRef.current?._id && firebaseUser?.email && firebaseUser?.displayName);

        if (needsProcessing) {
          setIsLoadingGoogleAuth(true);
        }

        try {
          if (
            localStorage.getItem('fusionFirstAccount') &&
            firebaseUser?.email &&
            firebaseUser?.displayName
          ) {
            console.info('Handling account merge...');
            await handleAccountMerge(firebaseUser);
          } else if (userRef.current?._id) {
            console.info('User already logged in, skipping...');
            setIsLoadingGoogleAuth(false);
            isProcessingRef.current = false;
          } else if (firebaseUser?.email && firebaseUser?.displayName) {
            console.info('New Google login detected...');
            await handleRedirectInfoAuthentication();
          } else {
            console.info('Firebase user exists but missing required fields');
            setIsLoadingGoogleAuth(false);
          }
        } catch (error) {
          console.error('Error in auth state change handler:', error);
          setIsLoadingGoogleAuth(false);
          isProcessingRef.current = false;
        }
      },
    );

    return () => {
      console.info('Cleaning up GLOBAL onAuthStateChanged listener');
      unsubscribeAuthStateChanged();
    };
  }, [handleAccountMerge, handleRedirectInfoAuthentication, navigate]);

  const initiateGoogleLogin = useCallback(async () => {
    if (localStorage.getItem('newuser')) {
      localStorage.removeItem('newuser');
    }

    setIsLoadingGoogleAuth(true);

    try {
      console.info('Initiating Google login...');
      await HandleGoogle(auth);
    } catch (error) {
      console.error('Google login initiation error:', error);
      setIsLoadingGoogleAuth(false);
      alert('Une erreur s\'est produite');
    }
  }, []);

  const value = {
    isLoadingGoogleAuth,
    initiateGoogleLogin,
  };

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
      {isLoadingGoogleAuth && <AnimatedLoader className="absolute top-0 left-0 w-full h-full" />}
    </GoogleAuthContext.Provider>
  );
};

export const useGoogleAuth = () => {
  const context = useContext(GoogleAuthContext);
  if (!context) {
    throw new Error('useGoogleAuth must be used within GoogleAuthProvider');
  }
  return context;
};

